//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import Redux from 'redux';

import { broadcastRootScopeEvent } from '@react-bridge/wrappers/legacy-events';
import {
  CONTENT_DRAWER_ACTIONS,
  ContentDrawerBroadcastCalls,
  IContentDrawerSettings,
} from '@redux/content-drawer/contentDrawerModel';
import { createAction } from '@utils/actions';

import { ReduxGetState } from 'app/curalate.store';

const setDrawerSettings = (drawerSettings: IContentDrawerSettings) =>
  createAction({
    type: CONTENT_DRAWER_ACTIONS.SET_DRAWER_SETTINGS,
    drawerSettings,
  });

const setPageDrawerSettings = (pageDrawerSettings: IContentDrawerSettings) =>
  createAction({
    type: CONTENT_DRAWER_ACTIONS.SET_PAGE_DRAWER_SETTINGS,
    pageDrawerSettings,
  });

export type ContentDrawerActions = ReturnType<
  typeof setDrawerSettings | typeof setPageDrawerSettings
>;

/**
 * We include the disableContentDrawer setting here explicitly
 * because some components rely on the value being `false` rather
 * than a falsy value.
 */
const buildDrawerSettings = (settings: IContentDrawerSettings = {}) => ({
  ...settings,
  disableContentDrawer: settings.disableContentDrawer ?? false,
});

export const contentDrawerActions = {
  /**
   * This helper lives in this action set for the sake of condensing all content drawer
   * functionality into one file, but all it does is fire the appropriate broadcast event
   * so the content-drawer-controller knows to open.
   *
   * @param isDrawerSticky - Dictates whether the drawer will stay open regardless of external
   * event broadcasters trying to close it. Note: This flag is needed to keep the drawer open
   * when clicking the Upload button.
   */
  openDrawer: (isDrawerSticky?: boolean) => () => {
    broadcastRootScopeEvent(
      ContentDrawerBroadcastCalls.OpenDrawer,
      isDrawerSticky,
    );
  },

  /**
   * The override settings for the content drawer are temporary settings
   * automatically cleared out after the drawer is closed.
   */
  setOverrideSettings:
    (settings: IContentDrawerSettings) =>
    (dispatch: Redux.Dispatch, getState: ReduxGetState) => {
      const { pageDrawerSettings } = getState().contentDrawer;
      const newSettings = buildDrawerSettings({
        ...pageDrawerSettings,
        ...settings,
      });

      dispatch(setDrawerSettings(newSettings));
      broadcastRootScopeEvent(
        ContentDrawerBroadcastCalls.SetOverrideSettings,
        newSettings,
      );

      return newSettings;
    },

  /**
   * Resets the content drawer back to the current page's base settings.
   */
  unsetOverrideSettings:
    () => (dispatch: Redux.Dispatch, getState: ReduxGetState) => {
      const { pageDrawerSettings } = getState().contentDrawer;
      const newSettings = buildDrawerSettings(pageDrawerSettings);

      dispatch(setDrawerSettings(newSettings));
      broadcastRootScopeEvent(
        ContentDrawerBroadcastCalls.UnsetOverrideSettings,
        newSettings,
      );

      return newSettings;
    },

  /**
   * The page settings settings are the defaults applied to the content
   * drawer on any given page. These are controlled by the pages themselves
   * and are used to reset the content drawer back to the original state
   * it was in upon initialization (per-page CTAs, filters, etc).
   */
  setPageSettings:
    (settings: IContentDrawerSettings) =>
    (dispatch: Redux.Dispatch, getState: ReduxGetState) => {
      const { drawerSettings } = getState().contentDrawer;
      const newSettings = buildDrawerSettings({
        ...drawerSettings,
        ...settings,
      });

      dispatch(setDrawerSettings(newSettings));
      dispatch(setPageDrawerSettings(newSettings));
      broadcastRootScopeEvent(
        ContentDrawerBroadcastCalls.SetPageSettings,
        newSettings,
      );

      return newSettings;
    },

  /**
   * Clears all settings from the content drawer.
   */
  unsetPageSettings: () => (dispatch: Redux.Dispatch) => {
    const newSettings = buildDrawerSettings();

    dispatch(setDrawerSettings(newSettings));
    dispatch(setPageDrawerSettings(newSettings));
    broadcastRootScopeEvent(
      ContentDrawerBroadcastCalls.UnsetPageSettings,
      newSettings,
    );

    return newSettings;
  },
};
